import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import { CodeDoor } from '../../../api/accessCodeDoor/accessCodeDoorApiTypes';
import { formatCodeDoorLabel } from './codeDoorLabel';

interface CodeDoorConfirmDialogProps {
    door: CodeDoor;
    /** `block` / `unblock` toggle the generic block; `remove` archives. */
    action: 'block' | 'unblock' | 'remove';
    loading: boolean;
    onConfirm: () => void;
    onClose: () => void;
}

/**
 * Confirmation for the code-door row actions. The block copy is deliberately
 * honest (plan A9): blocking stops the app from showing the code and makes
 * the object unbookable — it does NOT physically lock the door; real
 * revocation is changing the code on the lock and updating it here.
 */
const CodeDoorConfirmDialog = ({
    door,
    action,
    loading,
    onConfirm,
    onClose,
}: CodeDoorConfirmDialogProps) => {
    const { t } = useTranslation();

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const doorLabel = formatCodeDoorLabel(door.assetName, door.locationName, door.assetId);

    return createPortal(
        <div className={'fixed inset-0 z-[1300] flex items-center justify-center p-4'}>
            {/* Decorative click-to-close backdrop — keyboard users close via the
                Escape handler above, so it's hidden from assistive tech (S1082). */}
            <div
                className={'fixed inset-0 bg-black bg-opacity-50'}
                onClick={onClose}
                aria-hidden={'true'}
            />

            <div
                className={'relative z-10 bg-text-contrast rounded-lg shadow-xl w-full max-w-md'}
                role={'dialog'}
                aria-modal={'true'}
            >
                <div className={'p-6'}>
                    <h3 className={'text-lg font-semibold text-foreground mb-1'}>
                        {t(`code_doors.${action}_title`)}
                    </h3>
                    <p className={'text-[15px] font-medium text-foreground mb-3'}>
                        {doorLabel}
                    </p>
                    <p className={'text-sm text-foreground/60'}>
                        {t(`code_doors.${action}_description`)}
                    </p>
                </div>

                <div className={'px-6 py-4 flex flex-row-reverse gap-3'}>
                    <CustomButton
                        title={t(`code_doors.${action}_cta`)}
                        variant={action === 'remove' ? 'delete' : 'default'}
                        onClick={onConfirm}
                        loading={loading}
                    />
                    <button
                        type={'button'}
                        className={'text-sm font-medium text-foreground/70 px-4'}
                        onClick={onClose}
                    >
                        {t('code_doors.cancel')}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default CodeDoorConfirmDialog;
